import classNames from "classnames";
import { useEffect, useRef, useState } from "react";

import styles from "./SectionStickyNav.module.css";

const SectionStickyNav: React.FC<SectionStickyNavProps> = ({
  sections,
  children,
}) => {
  const { containerRef, currentSection } = useCurrentSectionOnScroll();
  useScrollToCurrentNavItem(containerRef, currentSection);

  return (
    <div className={styles.scrollContainer} ref={containerRef}>
      <nav className={styles.stickyNav}>
        <ul className={styles.stickyNavItems}>
          {sections.map((section) => (
            <li
              id={`sticky-nav-item-${section.id}`}
              className={classNames(styles.stickyNavItem, {
                [styles.stickyNavItemActive]: section.id === currentSection,
              })}
              key={section.id}
            >
              <a href={`#${section.id}`}>{section.title}</a>
            </li>
          ))}
        </ul>
      </nav>
      <div className={styles.sections}>{children}</div>
    </div>
  );
};

const useCurrentSectionOnScroll = () => {
  const [prevScrollPosition, setPrevScrollPosition] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentSection, setCurrentSection] = useState<string>("");
  useEffect(() => {
    const onEntries = (entries: IntersectionObserverEntry[]) => {
      const scrollPosition = containerRef.current?.scrollTop || 0;
      const direction = scrollPosition > prevScrollPosition ? "down" : "up";
      setPrevScrollPosition(scrollPosition);

      entries.forEach((entry) => {
        if (
          (direction === "down" && !entry.isIntersecting) ||
          (direction === "up" && entry.isIntersecting)
        ) {
          setCurrentSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(onEntries, {
      root: null,
      rootMargin: "-20% 0% -60% 0%",
      threshold: 0,
    });

    const sections = containerRef.current?.querySelectorAll("section") || [];
    sections.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      sections.forEach((section) => {
        observer.unobserve(section);
      });
    };
  }, []);

  return { containerRef, currentSection };
};

const useScrollToCurrentNavItem = (
  containerRef: React.RefObject<HTMLDivElement>,
  currentSection: string,
) => {
  useEffect(() => {
    if (!currentSection) {
      return;
    }

    const navItem = containerRef.current?.querySelector<HTMLElement>(
      `#sticky-nav-item-${currentSection}`,
    );

    if (!navItem) {
      return;
    }

    const isFirstChild = navItem.parentElement?.firstElementChild === navItem;

    navItem.parentElement?.scrollTo({
      left: isFirstChild ? 0 : navItem.offsetLeft - 20,
      behavior: "smooth",
    });
  }, [currentSection]);
};

type SectionStickyNavProps = {
  sections: Array<{ title: string; id: string }>;
};

export default SectionStickyNav;
